import { motion } from 'framer-motion';
import React, { useState, useEffect } from 'react';
import { FaArrowLeft, FaCalendar, FaClock, FaTag, FaUser } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { blogData } from '../data/blogData';
import './BlogDetail.css';
import { getApiUrl } from '../utils/api';

const formatBlogContent = (content) => {
  if (!content) return "";
  
  const lines = content.split('\n');
  let formatted = "";
  let inList = false;
  
  lines.forEach((line) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('*') || trimmed.startsWith('-') || trimmed.startsWith('•')) {
      if (!inList) {
        formatted += '<ul style="list-style-type: disc; margin-left: 25px; padding-left: 0; margin-top: 15px; margin-bottom: 15px;">';
        inList = true;
      }
      
      const itemText = trimmed.substring(1).trim();
      if (itemText) {
        const colonIndex = itemText.indexOf(':');
        if (colonIndex !== -1) {
          const boldPart = itemText.substring(0, colonIndex + 1);
          const restPart = itemText.substring(colonIndex + 1);
          formatted += `<li style="margin-bottom: 8px;"><strong>${boldPart}</strong>${restPart}</li>`;
        } else {
          formatted += `<li style="margin-bottom: 8px;">${itemText}</li>`;
        }
      }
    } else {
      if (inList) {
        formatted += '</ul>';
        inList = false;
      }
      if (trimmed) {
        formatted += `<p style="margin-bottom: 10px; opacity: 0.9; color: #ccc;">${trimmed}</p>`;
      }
    }
  });
  
  if (inList) {
    formatted += '</ul>';
  }
  
  return formatted;
};

const BlogDetail = () => {
  const { blogId } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const url = getApiUrl(`/api/blogs/${blogId}`);
    fetch(url, {
      headers: {
        'Bypass-Tunnel-Reminder': 'true'
      }
    })
      .then(res => res.json())
      .then(data => {
        console.log("Fetched blog detail:", data);
        setBlog(data);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching blog detail:", err);
        setLoading(false);
      });
  }, [blogId]);

  useEffect(() => {
    if (!loading) {
      window.scrollTo(0, 0);
      if (window.lenis) {
        window.lenis.scrollTo(0, { immediate: true });
      }
    }
  }, [loading]);

  if (loading) {
    return <div className="loading" style={{ padding: '100px', textAlign: 'center', color: '#fff' }}>Loading...</div>;
  }

  if (!blog) {
    return (
      <div className="blog-not-found">
        <h2>Blog Post Not Found</h2>
        <button onClick={() => navigate('/')}>Go Back Home</button>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <motion.div
      className="blog-detail"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Hero Section */}
      <div className="blog-hero">
        <motion.button
          className="back-button"
          onClick={() => {
            navigate('/', { state: { scrollTo: 'blogs', activeBlogId: blogId } });
          }}
          whileHover={{ scale: 1.05, x: -5 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaArrowLeft /> Back to Blogs
        </motion.button>
        
        <motion.div
          className="blog-hero-content"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="blog-hero-image">
            <img 
              src={blog.imageUrl || "/images/blog/default.png"} 
              alt={blog.title}
              className="hero-image"
            />
            {(blog.featured !== undefined ? blog.featured : (blogData.find(b => b.title.toLowerCase() === blog.title.toLowerCase())?.featured || false)) && (
              <span className="blog-detail-featured-badge">Featured</span>
            )}
          </div>
          
          <div className="blog-hero-info">
            <div className="blog-detail-meta">
              <span className="blog-detail-category">{blog.role || "Tech"}</span>
              <span className="blog-detail-date">
                <FaCalendar /> {new Date(blog.createdAt).toLocaleDateString()}
              </span>
            </div>
            
            <h1 className="blog-detail-title">{blog.title}</h1>
            <p className="blog-detail-excerpt">{blog.content?.substring(0, 100)}...</p>
            
            <div className="blog-author-info">
              <div className="author-details">
                <FaUser />
                <span>{blog.author}</span>
              </div>
              <div className="read-time">
                <FaClock />
                <span>{blog.readTime}</span>
              </div>
            </div>
            
            <div className="blog-detail-tags">
              {blog.techStack?.map((tag, index) => (
                <span key={index} className="blog-detail-tag">
                  <FaTag />
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Content Section */}
      <motion.div
        className="blog-detail-content"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="blog-article" variants={itemVariants}>
          <div dangerouslySetInnerHTML={{ __html: formatBlogContent(blog.content) }} />
          
          {blog.data && blog.data.length > 0 && (
            <div className="blog-subsections" style={{ marginTop: '30px' }}>
              {blog.data.map((section, index) => (
                <div key={index} className="blog-subsection" style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '1.5rem', marginBottom: '10px', color: '#fff' }}>{section.title}</h3>
                  <p style={{ opacity: 0.9, color: '#ccc' }} dangerouslySetInnerHTML={{ __html: formatBlogContent(section.description) }} />
                </div>
              ))}
            </div>
          )}
        </motion.div>
        
        {/* CTA Section */}
        <motion.div className="blog-cta" variants={itemVariants}>
          <h2>Enjoyed this article?</h2>
          <p>Explore more insights and stay updated with the latest trends</p>
          <motion.button
            className="cta-button-blog"
            onClick={() => {
              navigate('/');
              setTimeout(() => {
                document.getElementById('blogs')?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Read More Blogs
          </motion.button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default BlogDetail;
